/**
 * `<VoxelMesh>` — the shared instanced-cube renderer used by
 * `<VoxelDisplay>` and (in a follow-up) `<VoxelAnimation>`.
 *
 * Renders one `<instancedMesh>` per material slot (4 draw calls per
 * model regardless of voxel count). Same per-slot material tuning as
 * `ArchetypeMech` — emissive slot uses `meshStandardMaterial.emissive`
 * + higher intensity so it picks up Bloom. We don't share the
 * ArchetypeMech version yet because ArchetypeMech is mech-state-driven
 * (per-frame position tween); this is the static-display variant.
 */

import { useEffect, useRef } from "react";
import { Color, InstancedMesh, Matrix4, type Material } from "three";

import type { MaterialSlot, Voxel } from "../voxel/types";

const SLOT_ORDER: MaterialSlot[] = ["primary", "secondary", "accent", "emissive"];

export interface VoxelMeshProps {
  voxels: ReadonlyArray<Voxel>;
  /** Per-slot color overrides; falls back to the asset's slot_palette. */
  palette: Record<MaterialSlot, string>;
  /** World-units per voxel. Default 0.025 matches ArchetypeMech. */
  voxelScale?: number;
}

export function VoxelMesh({ voxels, palette, voxelScale = 0.025 }: VoxelMeshProps) {
  const buckets: Record<MaterialSlot, Voxel[]> = {
    primary: [],
    secondary: [],
    accent: [],
    emissive: [],
  };
  for (const v of voxels) buckets[v.slot].push(v);

  return (
    <group name="voxel-mesh">
      {SLOT_ORDER.map((slot) => (
        <SlotInstances
          key={slot}
          slot={slot}
          voxels={buckets[slot]}
          color={palette[slot]}
          voxelScale={voxelScale}
        />
      ))}
    </group>
  );
}

interface SlotInstancesProps {
  slot: MaterialSlot;
  voxels: ReadonlyArray<Voxel>;
  color: string;
  voxelScale: number;
}

function SlotInstances({ slot, voxels, color, voxelScale }: SlotInstancesProps) {
  const meshRef = useRef<InstancedMesh>(null);
  const count = voxels.length;

  useEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const m = new Matrix4();
    voxels.forEach((v, i) => {
      m.makeTranslation(v.x * voxelScale, v.y * voxelScale, v.z * voxelScale);
      mesh.setMatrixAt(i, m);
    });
    mesh.instanceMatrix.needsUpdate = true;
    mesh.castShadow = true;
    mesh.receiveShadow = true;

    const mat = mesh.material as Material & {
      color?: Color;
      emissive?: Color;
      emissiveIntensity?: number;
      metalness?: number;
      roughness?: number;
    };
    if (mat?.color && "set" in mat.color) {
      mat.color.set(color);
    }
    if (slot === "emissive") {
      mat.emissive?.set(color);
      if (mat.emissiveIntensity !== undefined) mat.emissiveIntensity = 1.4;
      if (mat.metalness !== undefined) mat.metalness = 0.1;
      if (mat.roughness !== undefined) mat.roughness = 0.4;
    } else {
      if (mat.metalness !== undefined) mat.metalness = slot === "primary" ? 0.6 : 0.3;
      if (mat.roughness !== undefined) mat.roughness = slot === "secondary" ? 0.7 : 0.5;
    }
  }, [voxels, color, slot, voxelScale]);

  if (count === 0) return null;

  return (
    <instancedMesh ref={meshRef} args={[undefined, undefined, count]} castShadow receiveShadow>
      <boxGeometry args={[voxelScale, voxelScale, voxelScale]} />
      <meshStandardMaterial color={color} />
    </instancedMesh>
  );
}
